import React, { useState, useEffect } from "react";
import { Calendar, Video, Users, Clock, Plus, Loader2, FileText, ExternalLink, Trash2, CheckCircle2, RefreshCw, AlertCircle } from "lucide-react";
import { Meeting, CRMPerson } from "../types";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../utils";
import { toast } from "sonner";
import { GoogleGenAI } from "@google/genai";
import { SectionPillHeader } from "./SectionPillHeader";

interface MeetingsProps {
  projectId: string;
  projectName: string;
}

export function Meetings({ projectId, projectName }: MeetingsProps) {
  const { user } = useAuth();
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [isSyncing, setIsSyncing] = useState<string | null>(null);
  const [isSummarizing, setIsSummarizing] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Form state
  const [title, setTitle] = useState(`Production Meeting - ${projectName}`);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("11:00");
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);
  const [availablePeople, setAvailablePeople] = useState<CRMPerson[]>([]);

  useEffect(() => {
    fetchMeetings();
    fetchPeople();
  }, [projectId]);

  const fetchMeetings = async (retries = 3) => {
    if (!user || !projectId) {
      setIsLoading(false);
      return;
    }
    
    try {
      const res = await fetch(`/api/projects/${projectId}/meetings`, {
        headers: { "x-user-uid": user.uid }
      });
      
      const contentType = res.headers.get("content-type");
      const text = await res.text();
      
      if (res.ok) {
        if (text && contentType && contentType.includes("application/json")) {
          try {
            const data = JSON.parse(text);
            setMeetings(data);
          } catch (e) {
            console.error("Failed to parse JSON:", e);
            toast.error("Received invalid JSON from server");
          }
        } else if (!text) {
          setMeetings([]);
        } else {
          console.error("Expected JSON but received:", text);
          toast.error("Received invalid response from server");
        }
        setIsLoading(false);
      } else {
        if (text && contentType && contentType.includes("application/json")) {
          try {
            const error = JSON.parse(text);
            console.error("Failed to fetch meetings:", error);
          } catch (e) {
            console.error("Failed to parse error JSON:", e);
          }
        } else {
          console.error("Failed to fetch meetings (non-JSON):", text);
        }
        setIsLoading(false);
      }
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => fetchMeetings(retries - 1), 2000);
      } else {
        console.error("Failed to fetch meetings:", error);
        setIsLoading(false);
      }
    }
  };

  const fetchPeople = async (retries = 3) => {
    if (!user) return;
    try {
      const res = await fetch("/api/crm/people", {
        headers: { "x-user-uid": user.uid }
      });
      if (res.ok) {
        const contentType = res.headers.get("content-type");
        const text = await res.text();
        if (text && contentType && contentType.includes("application/json")) {
          try {
            const data = JSON.parse(text);
            setAvailablePeople(data);
          } catch (e) {
            console.error("Failed to parse people JSON:", e);
          }
        }
      }
    } catch (error) {
      if (retries > 0) {
        setTimeout(() => fetchPeople(retries - 1), 2000);
      } else {
        console.error("Failed to fetch people:", error);
      }
    }
  };

  const handleCreateMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !projectId) {
      toast.error("Project ID is missing");
      return;
    }
    
    setIsCreating(true);
    try {
      const startDateTime = new Date(`${date}T${startTime}:00`).toISOString();
      const endDateTime = new Date(`${date}T${endTime}:00`).toISOString();

      const res = await fetch(`/api/projects/${projectId}/meetings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid
        },
        body: JSON.stringify({
          title,
          startTime: startDateTime,
          endTime: endDateTime,
          attendees: selectedAttendees,
          projectId
        })
      });

      const contentType = res.headers.get("content-type");
      const text = await res.text();
      
      if (res.ok) {
        toast.success("Meeting scheduled and Google Meet link generated!");
        setShowCreateModal(false);
        fetchMeetings();
      } else {
        if (text && contentType && contentType.includes("application/json")) {
          try {
            const error = JSON.parse(text);
            toast.error(error.message || error.error || "Failed to schedule meeting");
          } catch (e) {
            toast.error("Failed to schedule meeting: Invalid error response");
          }
        } else {
          if (text && text.includes("Rate exceeded")) {
            toast.error("Rate limit exceeded. Please wait a moment and try again.");
          } else {
            toast.error("Failed to schedule meeting: Server error");
          }
          console.error("Error creating meeting (non-JSON):", text);
        }
      }
    } catch (error) {
      console.error("Error creating meeting:", error);
      toast.error("An error occurred while scheduling the meeting");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteMeeting = async (meetingId: string) => {
    if (!user || !confirm("Are you sure you want to cancel this meeting?")) return;
    
    try {
      const res = await fetch(`/api/projects/${projectId}/meetings/${meetingId}`, {
        method: "DELETE",
        headers: { "x-user-uid": user.uid }
      });
      if (res.ok) {
        toast.success("Meeting cancelled");
        fetchMeetings();
      }
    } catch (error) {
      console.error("Failed to delete meeting:", error);
      toast.error("Failed to cancel meeting");
    }
  };

  const handleSyncRecording = async (meetingId: string) => {
    if (!user) return;
    setIsSyncing(meetingId);
    try {
      const res = await fetch(`/api/projects/${projectId}/meetings/${meetingId}/sync-recording`, {
        method: "POST",
        headers: { "x-user-uid": user.uid }
      });
      if (res.ok) {
        toast.success("Recording found and moved to project drive!");
        fetchMeetings();
      } else {
        const error = await res.json();
        toast.error(error.error || "Failed to sync recording");
      }
    } catch (error) {
      console.error("Error syncing recording:", error);
      toast.error("Failed to sync recording");
    } finally {
      setIsSyncing(null);
    }
  };

  const handleSummarize = async (meeting: Meeting) => {
    if (!user) return;
    setIsSummarizing(meeting.id);
    try {
      const apiKey = process.env.API_KEY || process.env.GEMINI_API_KEY || "";
      const ai = new GoogleGenAI({ apiKey });
      
      const prompt = `Summarize a production meeting titled "${meeting.title}" for project "${projectName}". 
      Attendees: ${meeting.attendees.join(', ')}. 
      The meeting took place from ${meeting.startTime} to ${meeting.endTime}.
      
      Please provide a professional summary including:
      1. Key discussion points
      2. Decisions made
      3. Action items
      
      Format the output as a concise markdown summary.`;

      const result = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: prompt,
      });
      
      const summary = result.text;

      // Save summary to database
      const res = await fetch(`/api/projects/${projectId}/meetings/${meeting.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid
        },
        body: JSON.stringify({ summary, status: "completed" })
      });

      if (res.ok) {
        toast.success("Meeting summary generated!");
        fetchMeetings();
      }
    } catch (error: any) {
      console.error("Error summarizing meeting:", error);
      if (error.message?.includes("429") || error.message?.includes("RESOURCE_EXHAUSTED")) {
        toast.error("Spending cap reached. Please connect a personal API key in the Video Analysis tab.");
      } else {
        toast.error("Failed to generate summary");
      }
    } finally {
      setIsSummarizing(null);
    }
  };

  return (
    <div className="flex flex-col h-full space-y-6">
      <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between shadow-sm shrink-0 mb-6">
        <SectionPillHeader
          icon={Calendar}
          title="Meetings"
          description="Schedule production meetings, sync with Google Calendar, and generate AI summaries"
        />
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm text-xs font-medium"
        >
          <Plus className="w-4 h-4" />
          Schedule Meeting
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
        </div>
      ) : meetings.length === 0 ? (
        <div className="rounded-2xl border-2 border-dotted border-slate-200 bg-white p-12 text-center dark:border-slate-700 dark:bg-slate-900">
          <Calendar className="mx-auto mb-4 h-12 w-12 text-slate-300 dark:text-slate-600" />
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">No meetings scheduled</h3>
          <p className="mt-1 text-slate-500 dark:text-slate-400">Schedule your first production meeting to get started.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-6 inline-flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 font-medium text-slate-700 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <Plus className="w-4 h-4" />
            Schedule Now
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {meetings.map((meeting) => (
            <div key={meeting.id} className="group overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-colors hover:border-indigo-200 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-indigo-500">
              <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-50 dark:bg-indigo-900/30">
                    <Video className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{meeting.title}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        {new Date(meeting.startTime).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        {new Date(meeting.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - 
                        {new Date(meeting.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Users className="w-4 h-4" />
                        {meeting.attendees.length} Attendees
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {meeting.meetLink && (
                    <a
                      href={meeting.meetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors font-bold text-sm"
                    >
                      <Video className="w-4 h-4" />
                      Join Meet
                    </a>
                  )}
                  
                  {!meeting.recordingUrl && new Date(meeting.endTime) < new Date() && (
                    <button
                      onClick={() => handleSyncRecording(meeting.id)}
                      disabled={isSyncing === meeting.id}
                      className="flex items-center gap-2 rounded-lg bg-slate-100 px-4 py-2 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-200 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                    >
                      {isSyncing === meeting.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                      Sync Recording
                    </button>
                  )}

                  {meeting.recordingUrl && !meeting.summary && (
                    <button
                      onClick={() => handleSummarize(meeting)}
                      disabled={isSummarizing === meeting.id}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-bold text-sm disabled:opacity-50"
                    >
                      {isSummarizing === meeting.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <FileText className="w-4 h-4" />
                      )}
                      Summarize
                    </button>
                  )}

                  <button
                    onClick={() => handleDeleteMeeting(meeting.id)}
                    className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {meeting.recordingUrl && (
                <div className="px-6 pb-4 flex items-center gap-4">
                  <a
                    href={meeting.recordingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View Recording in Drive
                  </a>
                  {meeting.transcriptUrl && (
                    <a
                      href={meeting.transcriptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-bold text-indigo-600 hover:underline"
                    >
                      <FileText className="w-3 h-3" />
                      View Transcript
                    </a>
                  )}
                </div>
              )}

              {meeting.summary && (
                <div className="border-t border-slate-100 bg-slate-50/30 px-6 pb-6 pt-2 dark:border-slate-800 dark:bg-slate-800/40">
                  <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-wider mb-2">
                    <FileText className="w-3.5 h-3.5" />
                    AI Summary
                  </div>
                  <div className="prose prose-sm max-w-none text-sm leading-relaxed text-slate-600 dark:prose-invert dark:text-slate-300">
                    {meeting.summary.split('\n').map((line, i) => (
                      <p key={i}>{line}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Meeting Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="animate-in fade-in zoom-in duration-200 w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 p-6 dark:border-slate-800 dark:bg-slate-800/50">
              <h3 className="font-brandon text-xl font-bold text-slate-900 dark:text-slate-100">Schedule Meeting</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
                <Trash2 className="w-5 h-5 rotate-45" />
              </button>
            </div>

            <form onSubmit={handleCreateMeeting} className="p-6 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Meeting Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 outline-none transition-all focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Date</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 outline-none transition-all focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Duration</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 outline-none transition-all focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                      required
                    />
                    <span className="text-slate-400 dark:text-slate-500">-</span>
                    <input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 outline-none transition-all focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Attendees</label>
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-2 dark:border-slate-700 dark:bg-slate-800">
                  {availablePeople.map((person) => (
                    <label key={person.id} className="flex cursor-pointer items-center gap-3 rounded-md p-2 transition-colors hover:bg-white dark:hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={selectedAttendees.includes(person.email || "")}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAttendees(prev => [...prev, person.email || ""]);
                          } else {
                            setSelectedAttendees(prev => prev.filter(email => email !== person.email));
                          }
                        }}
                        className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                      />
                      <div className="flex items-center gap-2">
                        {person.photoUrl ? (
                          <img src={person.photoUrl} alt={person.name} className="w-6 h-6 rounded-full" />
                        ) : (
                          <div className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-[10px] font-bold">
                            {person.name.charAt(0)}
                          </div>
                        )}
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{person.name}</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">{person.email}</span>
                        </div>
                      </div>
                    </label>
                  ))}
                  {availablePeople.length === 0 && (
                    <p className="p-2 text-center text-xs italic text-slate-500 dark:text-slate-400">No people found in CRM</p>
                  )}
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 rounded-lg border border-slate-200 px-4 py-2.5 font-bold text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Confirm Meeting
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
